import PropTypes from "prop-types";
import React, { useEffect } from "react";

import { Section } from "@swa-ui/core";
import { RadioButtonFormField, SelectFormField, useFormContext } from "@swa-ui/form";
import i18n from "@swa-ui/locale";

import { PlacementInfoLabel } from "../PlacementInfoLabel";
import styles from "./PersonalWheelchairAssistanceFormFields.module.scss";
import {
  PERSONAL_WHEELCHAIR_ASSISTANCE_SCHEMA_FIELD_NAMES,
  WHEELCHAIR_OPTIONS,
} from "./PersonalWheelchairAssistanceFormFields.schema";

const { NON_SPILLABLE_BATTERIES_COUNT, SPILLABLE_BATTERIES_COUNT, WHEELCHAIR_STOWAGE } =
  PERSONAL_WHEELCHAIR_ASSISTANCE_SCHEMA_FIELD_NAMES;

export const PersonalWheelchairAssistanceFormFields = (props) => {
  const {
    nonSpillableBatteriesFieldName = NON_SPILLABLE_BATTERIES_COUNT,
    spillableBatteriesFieldName = SPILLABLE_BATTERIES_COUNT,
    wheelchairStowageFieldName = WHEELCHAIR_STOWAGE,
  } = props;
  const { setValue, watch } = useFormContext();
  const selectedAssistance = watch(WHEELCHAIR_STOWAGE);

  useEffect(() => {
    selectedAssistance !== WHEELCHAIR_OPTIONS.WITH_NON_SPILLABLE_BATTERIES &&
      resetFormFields([nonSpillableBatteriesFieldName]);
    selectedAssistance !== WHEELCHAIR_OPTIONS.WITH_SPILLABLE_BATTERIES &&
      resetFormFields([spillableBatteriesFieldName]);
  }, [selectedAssistance]);

  return (
    <Section {...getSectionProps()}>
      <RadioButtonFormField {...getRadioButtonFormFieldProps()} />
    </Section>
  );

  function renderSelectField(fieldName) {
    return (
      <div className={styles.radioCaption}>
        <SelectFormField {...getSelectProps(fieldName)} />
        <span className={styles.batteryCounterText}>
          {i18n("PersonalWheelchairAssistanceFormFields__WHEELCHAIR_BATTERIES_COUNT_TEXT")}
        </span>
      </div>
    );
  }

  function getSectionProps() {
    return {
      headingProps: {
        bottomSpacing: "small",
        children: i18n("PersonalWheelchairAssistanceFormFields__HEADER"),
        headingLevel: 2,
        styleLevel: 5,
        topSpacing: "medium",
      },
    };
  }

  function getRadioButtonFormFieldProps() {
    return {
      componentProps: {
        ariaLiveInput: (name, index, length) =>
          `${i18n("PersonalWheelchairAssistanceFormFields__RADIO_BUTTON_ARIA_LIVE", {
            index,
            length,
            name,
          })}`,
        className: styles.radioGroup,
        direction: "column",
        radioButtonInfoList: [
          {
            label: i18n("PersonalWheelchairAssistanceFormFields__NO_WHEELCHAIR_STOWAGE"),
            value: WHEELCHAIR_OPTIONS.NO_WHEELCHAIR_STOWAGE,
          },
          {
            label: renderRadioLabel(
              "PersonalWheelchairAssistanceFormFields__MANUAL_WHEELCHAIR",
              "disabilityOptionWheelchairStowageManual"
            ),
            value: WHEELCHAIR_OPTIONS.MANUAL_WHEELCHAIR,
          },
          {
            ...(selectedAssistance === WHEELCHAIR_OPTIONS.WITH_NON_SPILLABLE_BATTERIES && {
              caption: renderSelectField(nonSpillableBatteriesFieldName),
            }),
            label: renderRadioLabel(
              "PersonalWheelchairAssistanceFormFields__WHEELCHAIR_WITH_NON_SPILLABLE_BATTERIES",
              "disabilityOptionWheelchairNonSpillableBatteries"
            ),
            value: WHEELCHAIR_OPTIONS.WITH_NON_SPILLABLE_BATTERIES,
          },
          {
            ...(selectedAssistance === WHEELCHAIR_OPTIONS.WITH_SPILLABLE_BATTERIES && {
              caption: renderSelectField(spillableBatteriesFieldName),
            }),
            label: renderRadioLabel(
              "PersonalWheelchairAssistanceFormFields__WHEELCHAIR_WITH_SPILLABLE_BATTERIES",
              "disabilityOptionWheelchairStowageSpillable"
            ),
            value: WHEELCHAIR_OPTIONS.WITH_SPILLABLE_BATTERIES,
          },
        ],
      },
      hideBottomSection: true,
      name: wheelchairStowageFieldName,
    };
  }

  function getSelectProps(fieldName) {
    return {
      componentProps: {
        list: [1, 2, 3, 4],
        maxItemsToDisplay: 4,
        size: "small",
      },
      hideBottomSection: true,
      hideTopSection: true,
      name: fieldName,
    };
  }

  function renderRadioLabel(label, placementId) {
    return <PlacementInfoLabel label={label} placementId={placementId} />;
  }

  function resetFormFields(fieldNames) {
    fieldNames?.map((fieldName) => setValue(fieldName, undefined));
  }
};

export const personalWheelchairAssistanceFormFieldsPropTypes = {
  /** Field name for non spillable batteries dropdown. */
  nonSpillableBatteriesFieldName: PropTypes.string,

  /** Field name for spillable batteries dropdown. */
  spillableBatteriesFieldName: PropTypes.string,

  /** Field name for personal wheel chair stowage radio group. */
  wheelchairStowageFieldName: PropTypes.string,
};

PersonalWheelchairAssistanceFormFields.propTypes = personalWheelchairAssistanceFormFieldsPropTypes;
